import {RRDate, Period} from "../../..";
import type {RRDuration} from "../../..";
import {getOfferedTimeSlots} from "../../..";

export type Timeslot = `${string}:${string}`;
// type Timeslots = Timeslot[];
type DateWithTimeslot = {date: RRDate; timeslots: Period[]};
export type DatesWithTimeslots = DateWithTimeslot[];
type DateKey = string;

export class Schedule {
  private availabilities: Map<DateKey, Period[]> = new Map();

  /**
   * @param date - date to check
   * @param duration - to see if any period is longer than
   * @returns true if there is a period longer than duration on given date
   */
  canFit(date: RRDate, duration: RRDuration): boolean {
    const durationInMillis = duration.toMillis();
    return (this.availabilities.get(date.toString("yyyy-MM-dd")) ?? []).some((av) => av.duration().toMillis() >= durationInMillis);
  }

  addPeriod(timeslot: Period): void {
    const dateKey = timeslot.from.toString("yyyy-MM-dd");
    const timeslots = this.availabilities.get(dateKey) ?? [];
    this.availabilities.set(dateKey, [...timeslots, timeslot]);
  }

  addPeriods(periods: Period[]): void {
    periods.forEach((period) => this.addPeriod(period));
  }

  private getCorrectFirstTime = (period: Period, duration: RRDuration): Period | undefined => {
    const scheduleForFirstAv = new Schedule();
    scheduleForFirstAv.addPeriod(new Period(period.from, period.to));
    const firstAV = scheduleForFirstAv.timeslotsByDay(new RRDate(period.from.toString("yyyy-MM-dd"), "yyyy-MM-dd"), duration);
    return firstAV[0];
  };

  /**
   * From a duration, return a reducer function that can be used to find the earliest date that can fit the duration
   */
  private _earliestReducer(duration: RRDuration) {
    return (currentEarliestDate: RRDate | "NO_AVAILABLE", period: Period): RRDate | "NO_AVAILABLE" => {
      const convertedPeriod = this.getCorrectFirstTime(period, duration);
      if (!convertedPeriod?.coversDuration(duration)) return currentEarliestDate;
      if (currentEarliestDate === "NO_AVAILABLE") return convertedPeriod.from;
      if (period.from.isBefore(currentEarliestDate)) return convertedPeriod.from;
      return currentEarliestDate;
    };
  }

  public firstDayAvailable(duration: RRDuration): RRDate | "NO_AVAILABLE" {
    const earliestAvailableDate = this.periods().reduce(this._earliestReducer(duration), "NO_AVAILABLE");
    return earliestAvailableDate;
  }

  timeslotsByDay(day: RRDate, duration: RRDuration): Period[] {
    const dayAvailabilities = this.availabilities.get(day.toString("yyyy-MM-dd")) ?? [];
    const timeslots = getOfferedTimeSlots(dayAvailabilities, duration);
    return timeslots;
  }

  private dateWithTimeslotByDate(date: RRDate, duration: RRDuration): DateWithTimeslot {
    const timeslots = this.timeslotsByDay(date, duration) ?? [];

    const dateWithTimeslot: DateWithTimeslot = {
      date: date,
      timeslots: timeslots,
    };
    return dateWithTimeslot;
  }

  private noTimeslots(dateWithTimeslot: DateWithTimeslot): boolean {
    return dateWithTimeslot.timeslots.length > 0;
  }

  public datesWithTimeslots(duration: RRDuration): DatesWithTimeslots {
    return this.dates()
      .map((date) => this.dateWithTimeslotByDate(date, duration))
      .filter(this.noTimeslots);
  }

  public datesAvilabilities(duration: RRDuration): RRDate[] {
    return this.dates().filter((day) => this.canFit(day, duration));
  }

  public dates(): RRDate[] {
    return Array.from(this.availabilities.keys()).map((date) => new RRDate(date, "yyyy-MM-dd"));
  }

  public periods = (): Period[] => {
    return [...this.availabilities.values()].flat();
  };

  public hasTimeslots(): boolean {
    return this.availabilities.size > 0;
  }
}
